import { useEffect, useState } from "react";
import {
  View,
  Text,
  Flex,
  ActionButton,
  Tooltip,
  TooltipTrigger,
} from "@adobe/react-spectrum";
import { useSelector } from "react-redux";
import Emitter from "../../../emitter";
import {
  API_CALL_CONFERENCE_HOLD,
  API_CALL_CONFERENCE_PLAY,
  API_CALL_REMOVE_CONNECTION,
  API_CALL_CONFERENCE_PARTICIPANT_MUTE,
} from "../../../constants/constants";
import { getConnectionIdByPhoneNumber } from "../../../amazon-connect/api/call";
import { RootState } from "../../../store/storeConfig";
import { IntervalType, SpeedDialData } from "../../../types/generic.types";
import IncomingCallIcon from "../../../assets/callIncomingIcon.svg";
import OutgoingCallIcon from "../../../assets/CallOutgoingIcon.svg";
import { formattedTime } from "../../../utils";
import { tabAccessControl } from "../../../utils/utils";
import {
  CallHangupIcon,
  MuteIcon,
  PauseIcon,
  PlayIcon,
  UnMmuteIcon,
} from "../../common/Icons";
import storeRTK from "../../../store/storeRTK";
import { setConference } from "../../../actions/call";

const ConferenceLiveCall = () => {
  const [callTime, setCallTime] = useState(0);
  const conference = useSelector(
    (state: RootState) => (state as any).conference
  );
  const call = useSelector((state: RootState) => state.call);
  const speedDials = useSelector(
    (state: RootState) => state.history.speedDials
  );
  const isAgentSelfCallHeld = useSelector(
    (state: RootState) => state.session.isAgentSelfCallHeld
  );

  useEffect(() => {
    let timerId: IntervalType = null;
    if (conference?.self?.state === "ESTABLISHED") {
      const startTime = conference?.self.connectedTime;
      timerId = setInterval(() => {
        setCallTime(Date.now() - startTime);
      }, 1000);
    } else {
      if (timerId !== null) {
        clearInterval(timerId);
      }
    }
    return () => {
      if (timerId !== null) {
        clearInterval(timerId);
      }
    };
  }, [conference?.self?.state]);

  if (!conference) return null;

  const hangupConferenceHandler = () => {
    const conferencePhoneNumber =
      conference?.phoneNumber || conference?.customer?.phoneNumber;
    const connectionId = getConnectionIdByPhoneNumber(conferencePhoneNumber);

    if (connectionId) {
      Emitter.emit(API_CALL_REMOVE_CONNECTION, {
        body: {
          connectionId,
          conversationId: conference.id,
        },
      });
    }
  };

  const muteConferenceHandler = () => {
    // Emitter.emit(API_CALL_MUTE, {
    //   body: { mute: !conference?.self?.muted, conversationId: conference.id },
    // });
    Emitter.emit(API_CALL_CONFERENCE_PARTICIPANT_MUTE, {
      body: {
        conversationId: conference.id,
        connectionId: conference.self.id || conference.id,
      },
    });
  };

  const toggleConferenceHold = () => {
    if (!conference?.id) return;
    const isCurrentlyHeld = Boolean(conference.self.held);
    const nextHeld = !isCurrentlyHeld;
    const event = nextHeld
      ? API_CALL_CONFERENCE_HOLD
      : API_CALL_CONFERENCE_PLAY;
    Emitter.emit(event, {
      body: { held: nextHeld, conversationId: conference.id },
    });
    storeRTK.dispatch(
      setConference({
        ...conference,
        self: {
          ...conference.self,
          held: nextHeld,
        },
        customer: {
          ...conference.customer,
          held: nextHeld,
        },
      })
    );
  };

  const getCustomerName = () => {
    // Check if quickConnectName is available first
    if (conference?.self?.quickConnectName) {
      return conference.self.quickConnectName;
    }

    // Fall back to existing logic
    const phoneNumber = conference?.customer?.phoneNumber;
    const speedDialEntry = speedDials?.find(
      (entry: SpeedDialData) => entry?.phoneNumber === phoneNumber
    );
    if (speedDialEntry && speedDialEntry.firstName && speedDialEntry.lastName) {
      return `${speedDialEntry.firstName} ${speedDialEntry.lastName}`;
    }
    return "";
  };

  const getCustomerPhone = () => {
    if (conference?.self?.quickConnectName) return;
    else return conference?.customer?.phoneNumber;
  };

  const hasCustomerName = () => {
    // Check if quickConnectName is available first
    if (conference?.self?.quickConnectName) {
      return true;
    }
    // Fall back to existing logic
    const phoneNumber = conference?.customer?.phoneNumber;
    const speedDialEntry = speedDials?.find(
      (entry: SpeedDialData) => entry?.phoneNumber === phoneNumber
    );
    return !!(
      speedDialEntry &&
      speedDialEntry.firstName &&
      speedDialEntry.lastName
    );
  };

  const getBorderColor = () => {
    if (conference?.self?.held) return "static-yellow-500";
    if (conference?.self?.state === "ESTABLISHED") {
      return "static-green-500";
    }
    return "static-blue-500";
  };

  return (
    <View
      borderColor="gray-200"
      borderWidth="thin"
      backgroundColor="gray-100"
      borderRadius={"regular"}
      UNSAFE_className="call-inProgress-container"
    >
      <View
        borderStartWidth="thicker"
        borderStartColor={getBorderColor()}
        borderRadius="regular"
        UNSAFE_className="call-inProgress-mid-container"
      >
        <Flex
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          height="100%"
        >
          <Flex direction="column">
            <Flex direction="row" alignItems="center" gap="size-100">
              <Text UNSAFE_className="call-inProgress-heading">
                {hasCustomerName() ? getCustomerName() : getCustomerPhone()}
              </Text>
            </Flex>
            {hasCustomerName() && (
              <Text UNSAFE_className="call-inProgress-font">
                {getCustomerPhone()}
              </Text>
            )}
            <Text UNSAFE_className="call-inProgress-font">
              <Flex
                direction="row"
                alignItems="center"
                justifyContent={"start"}
              >
                <Text marginX={3}>
                  {conference?.self?.state === "DIALING"
                    ? "Dialing"
                    : conference?.self?.held
                    ? "on hold"
                    : "on call"}{" "}
                </Text>
                <Text UNSAFE_className="call-inprogress-timer" marginX={2}>
                  {formattedTime(callTime)}
                </Text>
                <Text>
                  {conference?.self?.direction === "OUTBOUND" && (
                    <img
                      className="call-inProgress-icon"
                      src={OutgoingCallIcon}
                      alt="OutgoingCallIcon"
                    />
                  )}
                  {conference?.self?.direction === "INBOUND" && (
                    <img
                      className="call-inProgress-icon"
                      src={IncomingCallIcon}
                      alt="IncomingCallIcon"
                    />
                  )}
                </Text>
              </Flex>
            </Text>
          </Flex>
          <Flex direction={"row"}>
            {call?.id && conference?.id && (
              <Flex
                direction={"column"}
                alignItems="center"
                UNSAFE_className={`call-action-items ${
                  conference?.self?.muted ? "muted" : ""
                }`}
              >
                <TooltipTrigger>
                  <div style={{ display: "inline-block" }}>
                    <ActionButton
                      UNSAFE_className={
                        conference?.self?.muted
                          ? "iconButton active "
                          : "iconButton"
                      }
                      onPress={
                        (
                          tabAccessControl("contact_monitor") === "true"
                            ? false
                            : call.beingBarged ||
                              (call.id && conference?.id && isAgentSelfCallHeld)
                        )
                          ? () => {}
                          : muteConferenceHandler
                      }
                      isQuiet
                      UNSAFE_style={{
                        background: "red",
                        cursor: (
                          tabAccessControl("contact_monitor") === "true"
                            ? false
                            : call.beingBarged ||
                              (call.id && conference?.id && isAgentSelfCallHeld)
                        )
                          ? "not-allowed"
                          : "pointer",
                        backgroundColor: "#eaeaea",
                        opacity: (
                          tabAccessControl("contact_monitor") === "true"
                            ? false
                            : call.beingBarged ||
                              (call.id && conference?.id && isAgentSelfCallHeld)
                        )
                          ? 0.5
                          : 1,
                      }}
                    >
                      {conference?.self?.muted ? <MuteIcon /> : <UnMmuteIcon />}
                    </ActionButton>
                  </div>
                  <Tooltip>
                    {(
                      tabAccessControl("contact_monitor") === "true"
                        ? false
                        : call.beingBarged ||
                          (call.id && conference?.id && isAgentSelfCallHeld)
                    )
                      ? "Mute disabled"
                      : conference?.self?.muted
                      ? "Unmute support engineer"
                      : "Mute support engineer"}
                  </Tooltip>
                </TooltipTrigger>
                <Text UNSAFE_className="call-inProgress-font">
                  {conference?.self?.muted ? "Unmute" : "Mute"}
                </Text>
              </Flex>
            )}
            <Flex
              direction={"column"}
              alignItems="center"
              UNSAFE_className="call-action-items"
            >
              <TooltipTrigger>
                <div style={{ display: "inline-block" }}>
                  <ActionButton
                    UNSAFE_className={"iconButton"}
                    onPress={
                      (
                        tabAccessControl("contact_monitor") === "true"
                          ? false
                          : call.beingBarged ||
                            (call.id && conference?.id && isAgentSelfCallHeld)
                      )
                        ? () => {}
                        : toggleConferenceHold
                    }
                    UNSAFE_style={{
                      cursor: (
                        tabAccessControl("contact_monitor") === "true"
                          ? false
                          : call.beingBarged ||
                            (call.id && conference?.id && isAgentSelfCallHeld)
                      )
                        ? "not-allowed"
                        : "pointer",
                      backgroundColor: "#eaeaea",
                      opacity: (
                        tabAccessControl("contact_monitor") === "true"
                          ? false
                          : call.beingBarged ||
                            (call.id && conference?.id && isAgentSelfCallHeld)
                      )
                        ? 0.5
                        : 1,
                    }}
                    isQuiet
                  >
                    {conference?.self?.held ? <PlayIcon /> : <PauseIcon />}
                  </ActionButton>
                </div>
                <Tooltip>
                  {(
                    tabAccessControl("contact_monitor") === "true"
                      ? false
                      : call.beingBarged ||
                        (call.id && conference?.id && isAgentSelfCallHeld)
                  )
                    ? "Hold disabled"
                    : conference?.self?.held
                    ? "Resume support engineer call"
                    : "Hold support engineer call"}
                </Tooltip>
              </TooltipTrigger>

              <Text UNSAFE_className="call-inProgress-font">
                {conference?.self?.held ? "Resume" : "Hold"}
              </Text>
            </Flex>
            <Flex
              direction={"column"}
              alignItems="center"
              UNSAFE_className="call-action-items"
            >
              <TooltipTrigger>
                <div style={{ display: "inline-block" }}>
                  <ActionButton
                    UNSAFE_className={"call-end-Button"}
                    onPress={
                      (
                        tabAccessControl("contact_monitor") === "true"
                          ? false
                          : call.beingBarged ||
                            (call.id && conference?.id && isAgentSelfCallHeld)
                      )
                        ? () => {}
                        : hangupConferenceHandler
                    }
                    isQuiet
                    UNSAFE_style={{
                      background: "red",
                      cursor: (
                        tabAccessControl("contact_monitor") === "true"
                          ? false
                          : call.beingBarged ||
                            (call.id && conference?.id && isAgentSelfCallHeld)
                      )
                        ? "not-allowed"
                        : "pointer",
                      opacity: (
                        tabAccessControl("contact_monitor") === "true"
                          ? false
                          : call.beingBarged ||
                            (call.id && conference?.id && isAgentSelfCallHeld)
                      )
                        ? 0.5
                        : 1,
                    }}
                  >
                    <CallHangupIcon />
                  </ActionButton>
                </div>
                <Tooltip>
                  {(
                    tabAccessControl("contact_monitor") === "true"
                      ? false
                      : call.beingBarged ||
                        (call.id && conference?.id && isAgentSelfCallHeld)
                  )
                    ? "End call disabled"
                    : "End support engineer call"}
                </Tooltip>
              </TooltipTrigger>
              <Text UNSAFE_className="call-inProgress-font">End</Text>
            </Flex>
          </Flex>
        </Flex>
      </View>
    </View>
  );
};

export default ConferenceLiveCall;
